// Legacy: CumplimientoSinOcViewImpl.java — popup "Ingresar Gestión" (panelPopupCmpl / dialogoEditCmpl)
import {
  Component,
  signal,
  inject,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';
import { LoadingOverlayComponent } from '../shared/loading-overlay.component';
import { DropZoneDirective } from '../shared/file-uploader/drop-zone.directive';
import {
  BusquedaRespuestaDialogComponent,
  RespuestaLlamadaSeleccionada
} from '../common/busquedas/busqueda-respuesta-dialog.component';
import { BusquedaTipoAccesoDialogComponent } from '../common/busquedas/busqueda-tipo-acceso-dialog.component';
import { TipoCanal } from 'src/app/domain/service/tipo-canal/tipo-canal-repository';
import { SinOcRepository } from 'src/app/domain/service/sin-oc/sin-oc-repository';
import { GestionLlamadaRequest } from 'src/app/domain/model/sin-oc/sin-oc';
import { sanitizeText, sanitizeTextarea } from 'src/app/common/input-utils';
import { MAX_DNI_BENEFICIARIO, MAX_OBSERVACIONES_GESTION_SIN_OC } from 'src/app/common/constants-ejb';
import { HoraHintComponent } from 'src/app/presentation/shared/hora-hint/hora-hint.component';
import { ContadorCaracteresDirective } from 'src/app/presentation/shared/contador-caracteres/contador-caracteres.directive';

/** Estado interno del formulario de Ingresar Gestión */
interface GestionForm {
  nmconsecOficina: number | null;
  /** DNI completo del beneficiario (legacy: M094_CUERPOLIZA.DNI) */
  dniBeneficiario: string;
  // Campos de la llamada
  fechaLlamada: Date | null;
  horaLlamada: Date | null;
  dsrespondeLlamada: string;
  nmconsecRespuestaLlamada: number | null;
  dsrespuesta: string;
  /** S/N — indica si la respuesta es de tipo "cliente" (muestra motivo). Legacy: snaplica_nueva_llamada */
  esRespuestaCliente: boolean;
  nmconsecValoresLista: number | null;
  dsTipoCanal: string;
  fechaNuevaLlamada: Date | null;
  horaNuevaLlamada: Date | null;
  dsobservaciones: string;
  dsmotivoReporte: string;
  // Adjunto opcional
  adjuntoNombre: string | null;
  adjuntoArchivo: File | null;
}

@Component({
  selector: 'app-ingresar-gestion-dialog',
  standalone: true,
  imports: [
    ContadorCaracteresDirective,
    HoraHintComponent,
    DropZoneDirective,
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    DatePickerModule,
    LoadingOverlayComponent,
    BusquedaRespuestaDialogComponent,
    BusquedaTipoAccesoDialogComponent,
    TooltipModule,
    DropdownModule
  ],
  template: `
    <app-loading-overlay [blocked]="loading()" />

    <!-- Legacy: dialogoEditCmpl — panelPopupCmpl -->
    <p-dialog
      header="Ingresar Gestión"
      [visible]="visible()"
      (visibleChange)="visible.set($event)"
      [modal]="true"
      [closable]="true"
      [style]="{ width: '92vw', maxWidth: '36rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      (onHide)="onDialogHide()"
    >
      <!-- Mensaje de error inline -->
      @if (mensajeError()) {
        <div
          #errorBanner
          class="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-red-700 dark:text-red-300 text-sm"
          tabindex="-1"
        >
          <i class="pi pi-exclamation-circle mr-2"></i>{{ mensajeError() }}
        </div>
      }

      <div class="grid gap-4 p-1">
        <!-- ── Fecha y hora de la llamada (dfLlamada + stpAppointmentTime) ── -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Fecha de llamada <span class="text-red-500">*</span>
            </label>
            <!-- Legacy: dfLlamada inicializado con new Date() -->
            <p-datepicker
              [(ngModel)]="form.fechaLlamada"
              dateFormat="dd/mm/yy"
              [showIcon]="true"
              [maxDate]="hoy"
              appendTo="body"
              [showTransitionOptions]="'0ms'"
              [hideTransitionOptions]="'0ms'"
              styleClass="w-full"
              placeholder="dd/mm/aaaa"
            />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Hora de llamada <span class="text-red-500">*</span>
            </label>
            <!-- Legacy: stpAppointmentTime (24h) inicializado con hora actual -->
            <p-datepicker
              [(ngModel)]="form.horaLlamada"
              [timeOnly]="true"
              [hourFormat]="'24'"
              appendTo="body"
              [showTransitionOptions]="'0ms'"
              [hideTransitionOptions]="'0ms'"
              styleClass="w-full"
              placeholder="HH:mm"
            />
            <app-hora-hint [id]="'hora-llamada-ayuda'" />
          </div>
        </div>

        <!-- ── Quien atendió (stAtendio → dsresponde_llamada) ── -->
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Quien atendió
          </label>
          <input
            pInputText
            [(ngModel)]="form.dsrespondeLlamada"
            (change)="
              form.dsrespondeLlamada = sanitizeText(form.dsrespondeLlamada)
            "
            maxlength="100"
            class="w-full"
            placeholder="Nombre de quien atendió la llamada"
          />
        </div>

        <!-- ── Respuesta de la llamada (listLlamada → tbRespuesta) ── -->
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Respuesta de la llamada <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-1 min-w-0">
            <input
              pInputText
              readonly
              [value]="form.dsrespuesta || ''"
              placeholder="Buscar respuesta..."
              class="flex-1 min-w-0 cursor-pointer"
              (click)="dialogRespuestaVisible = true"
            />
            <button
              pButton
              icon="pi pi-search"
              class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
              pTooltip="Buscar respuesta"
              (click)="dialogRespuestaVisible = true"
            ></button>
            @if (form.dsrespuesta) {
              <button
                pButton
                icon="pi pi-times"
                class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar"
                (click)="limpiarRespuestaLlamada()"
              ></button>
            }
          </div>
        </div>

        <!-- ── Tipo de canal (tbTipoCanal → NMCONSEC_VALORES_LISTA) ── -->
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Tipo de canal <span class="text-red-500">*</span>
          </label>
          <div class="flex gap-1 min-w-0">
            <input
              pInputText
              readonly
              [value]="form.dsTipoCanal || ''"
              placeholder="Buscar tipo de canal..."
              class="flex-1 min-w-0 cursor-pointer"
              (click)="dialogTipoCanalVisible = true"
            />
            <button
              pButton
              icon="pi pi-search"
              class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
              pTooltip="Buscar tipo de canal"
              (click)="dialogTipoCanalVisible = true"
            ></button>
            @if (form.dsTipoCanal) {
              <button
                pButton
                icon="pi pi-times"
                class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar"
                (click)="limpiarTipoCanal()"
              ></button>
            }
          </div>
        </div>

        <!-- ── Fecha y hora de próxima llamada (dfnLlamada + stpnAppointmentTime) ── -->
        <!-- Legacy: rowName visible solo cuando snaplica_nueva_llamada == 'S' -->
        @if (proxLlamadaVisible) {
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Fecha próxima llamada
              </label>
              <p-datepicker
                [(ngModel)]="form.fechaNuevaLlamada"
                dateFormat="dd/mm/yy"
                [showIcon]="true"
                styleClass="w-full"
                placeholder="dd/mm/aaaa"
              />
            </div>
            <div>
              <label
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Hora próxima llamada
              </label>
              <p-datepicker
                [(ngModel)]="form.horaNuevaLlamada"
                [timeOnly]="true"
                [hourFormat]="'24'"
                styleClass="w-full"
                placeholder="HH:mm"
              />
              <app-hora-hint [id]="'hora-nueva-llamada-ayuda'" />
            </div>
          </div>
        }

        <!-- ── Observaciones (tbObservacion → dsobservaciones) ── -->
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Observaciones <span class="text-red-500">*</span>
          </label>
          <textarea
            [(ngModel)]="form.dsobservaciones"
            (change)="
              form.dsobservaciones = sanitizeTextarea(form.dsobservaciones)
            "
            rows="3"
            [appContadorCaracteres]="maxObservaciones"
            class="p-inputtext w-full"
            placeholder="Ingrese las observaciones de la gestión..."
          >
          </textarea>
        </div>

        <!-- ── Motivo de reporte (lbMotivoReporte) — solo si esRespuestaCliente (nmconsec==21) ── -->
        <!-- Legacy: SuraListBox con 4 opciones fijas de TiposMotivosReportes -->
        @if (form.esRespuestaCliente) {
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Motivo de reporte <span class="text-red-500">*</span>
            </label>
            <p-dropdown
              [(ngModel)]="form.dsmotivoReporte"
              [options]="MOTIVOS_REPORTE"
              optionLabel="label"
              optionValue="value"
              placeholder="Seleccione un motivo..."
              styleClass="w-full"
              appendTo="body"
              (onChange)="onMotivoReporteChange()"
            >
            </p-dropdown>
          </div>
        }

        <!-- ── Adjunto (uploaderAdjunto) — Legacy: visible solo si código en {19,20,21} ── -->
        <!-- Si código==21 (NMCODIGO_CLIENTE_REPORTADO): solo si motivo=="Posible Agrupadora" -->
        @if (adjuntoVisible) {
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Adjunto <span class="text-gray-400 font-normal">(opcional)</span>
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Formatos permitidos: PDF, JPG, PNG, DOC, XLS, PPT, DOCX, XLSX,
              PPTX, CSV &nbsp;·&nbsp; Tamaño máximo: 10 MB
            </p>
            <input
              type="file"
              class="hidden"
              #fileInput
              accept=".pdf,.jpg,.jpeg,.png,.doc,.xls,.ppt,.docx,.xlsx,.pptx,.csv"
              (change)="onAdjuntoSeleccionado($event)"
            />
            <div
              appDropZone
              (archivoSoltado)="procesarAdjunto($event)"
              class="flex flex-wrap items-center gap-2 p-2 w-full border border-dashed border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
            >
              <button
                pButton
                type="button"
                label="Seleccionar archivo"
                icon="pi pi-upload"
                class="p-button-outlined min-h-[44px]"
                (click)="fileInput.click()"
              ></button>
              @if (form.adjuntoNombre) {
                <span
                  class="text-sm text-gray-600 dark:text-gray-400 flex-1 min-w-0 truncate"
                  >{{ form.adjuntoNombre }}</span
                >
                <button
                  pButton
                  icon="pi pi-times"
                  class="p-button-text p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                  pTooltip="Quitar adjunto"
                  (click)="limpiarAdjunto()"
                ></button>
              } @else {
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >o arrastre el archivo aquí</span
                >
              }
            </div>
          </div>
        }
        <!-- /adjuntoVisible -->
      </div>
      <!-- /grid -->

      <!-- Footer del diálogo -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <button
            pButton
            label="Cancelar"
            icon="pi pi-times"
            class="p-button-outlined p-button-secondary min-h-[44px]"
            [disabled]="loading()"
            (click)="cancelar()"
          ></button>
          <button
            pButton
            label="Ingresar"
            icon="pi pi-check"
            class="p-button-primary min-h-[44px]"
            [disabled]="loading()"
            (click)="onIngresarGestion()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- ── Sub-diálogos de búsqueda ── -->
    <app-busqueda-respuesta-dialog
      [(visible)]="dialogRespuestaVisible"
      (onSelect)="onRespuestaLlamadaSeleccionada($event)"
    />

    <app-busqueda-tipo-acceso-dialog
      [(visible)]="dialogTipoCanalVisible"
      (onSelect)="onTipoCanalSeleccionado($event)"
    />
  `
})
export class IngresarGestionDialogComponent implements OnChanges {
  readonly maxObservaciones = MAX_OBSERVACIONES_GESTION_SIN_OC;
  // ── XSS Sanitization ───────────────────────────────────────────────────────
  sanitizeText = sanitizeText;
  sanitizeTextarea = sanitizeTextarea;

  // ── Ports (Hexagonal) ──────────────────────────────────────────────────────
  private readonly sinOcRepository = inject(SinOcRepository);

  // ── I/O ───────────────────────────────────────────────────────────────────
  @Input() datosBase: any;
  @Output() gestionCreada = new EventEmitter<any>();
  /** Emitido cuando el diálogo se cierra (por botón Cancelar, X o éxito) */
  @Output() onClose = new EventEmitter<void>();

  // ── Constantes Legacy ─────────────────────────────────────────────────────
  /** Constants.NMCODIGO_CLIENTE_REPORTADO = 21 */
  private readonly NMCODIGO_CLIENTE_REPORTADO = 21;
  /** Constants.NMCODIGOS_VALIDOS_SUBIR_EVIDENCIA = {19, 20, 21} */
  private readonly NMCODIGOS_VALIDOS_ADJUNTO = [19, 20, 21];
  /** ConstantsEJB.TiposMotivosReportes — opciones de lbMotivoReporte */
  readonly MOTIVOS_REPORTE = [
    { label: 'Posible Agrupadora', value: 'Posible Agrupadora' },
    { label: 'En proceso de liquidacion', value: 'En proceso de liquidacion' },
    { label: 'Menos de 10 trabajadores', value: 'Menos de 10 trabajadores' },
    { label: 'Otros', value: 'Otros' }
  ];

  // ── Estado ─────────────────────────────────────────────────────────────────
  @ViewChild('errorBanner') errorBanner?: ElementRef<HTMLDivElement>;

  /** Fecha máxima seleccionable en el calendario de llamada — hoy inclusive. */
  readonly hoy = (() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  })();

  visible = signal(false);
  loading = signal(false);
  mensajeError = signal<string | null>(null);

  dialogRespuestaVisible = false;
  dialogTipoCanalVisible = false;
  /** Legacy: rowName.clearDisplay() cuando snaplica_nueva_llamada=='S' */
  proxLlamadaVisible = false;
  /** Legacy: validarSubirEvidencias() — códigos {19,20,21} + motivo para 21 */
  adjuntoVisible = false;

  form: GestionForm = this.formVacio();

  // ── Ciclo de vida ──────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    // Legacy: postOpenDialog() — inicializa fechas y datos base al abrir el popup
    if (changes['datosBase'] && this.datosBase) {
      this.inicializar();
    }
  }

  private inicializar(): void {
    const ahora = new Date(); // Legacy: iniciarFechas() con new Date()
    this.form = {
      ...this.formVacio(),
      nmconsecOficina: this.datosBase.nmconsecOficina ?? null,
      dniBeneficiario: this.datosBase.dniBeneficiario?.trim() ?? '',
      // Legacy: dfLlamada = new Date() / stpAppointmentTime = hora actual
      fechaLlamada: ahora,
      horaLlamada: ahora
    };
    this.proxLlamadaVisible = false;
    this.adjuntoVisible = false;
    this.mensajeError.set(null);
    this.visible.set(true);
  }

  private formVacio(): GestionForm {
    return {
      nmconsecOficina: null,
      dniBeneficiario: '',
      fechaLlamada: null,
      horaLlamada: null,
      dsrespondeLlamada: '',
      nmconsecRespuestaLlamada: null,
      dsrespuesta: '',
      esRespuestaCliente: false,
      nmconsecValoresLista: null,
      dsTipoCanal: '',
      fechaNuevaLlamada: null,
      horaNuevaLlamada: null,
      dsobservaciones: '',
      dsmotivoReporte: '',
      adjuntoNombre: null,
      adjuntoArchivo: null
    };
  }

  // ── Selección de respuesta de llamada ──────────────────────────────────────

  onRespuestaLlamadaSeleccionada(res: RespuestaLlamadaSeleccionada): void {
    this.form.nmconsecRespuestaLlamada = res.nmconsecutivo;
    this.form.dsrespuesta = res.dsvalor;
    // Legacy: esRespuestaCliente → nmconsecutivo == NMCODIGO_CLIENTE_REPORTADO (21)
    // NO es snaplica_nueva_llamada — son dos flags independientes
    this.form.esRespuestaCliente =
      res.nmconsecutivo === this.NMCODIGO_CLIENTE_REPORTADO;
    if (!this.form.esRespuestaCliente) {
      this.form.dsmotivoReporte = '';
    }
    // Legacy: rowName visible cuando snaplica_nueva_llamada == 'S'
    this.proxLlamadaVisible = res.snaplicaNuevaLlamada === 'S';
    if (!this.proxLlamadaVisible) {
      this.form.fechaNuevaLlamada = null;
      this.form.horaNuevaLlamada = null;
    }
    // Legacy: validarSubirEvidencias(codigo) — adjunto según códigos {19,20,21}
    this.actualizarAdjuntoVisible();
    this.dialogRespuestaVisible = false;
  }

  limpiarRespuestaLlamada(): void {
    this.form.nmconsecRespuestaLlamada = null;
    this.form.dsrespuesta = '';
    this.form.esRespuestaCliente = false;
    this.form.dsmotivoReporte = '';
    this.proxLlamadaVisible = false;
    this.form.fechaNuevaLlamada = null;
    this.form.horaNuevaLlamada = null;
    this.adjuntoVisible = false;
    this.limpiarAdjunto();
  }

  // ── Selección de tipo de canal ─────────────────────────────────────────────

  onTipoCanalSeleccionado(tipo: TipoCanal): void {
    // Legacy: tbTipoCanal — el código se extrae de "CODIGO - DESCRIPCION" con split("-")[0].trim()
    // En nuestra API el código ya viene separado en codigo
    this.form.nmconsecValoresLista = tipo.codigo;
    this.form.dsTipoCanal = tipo.descripcion;
    this.dialogTipoCanalVisible = false;
  }

  limpiarTipoCanal(): void {
    this.form.nmconsecValoresLista = null;
    this.form.dsTipoCanal = '';
  }

  // ── Adjunto ────────────────────────────────────────────────────────────────

  /** Extensiones permitidas — igual que UtilidadSecurityActaFile.ALLOWED en el backend */
  private readonly EXTENSIONES_PERMITIDAS = new Set([
    'pdf',
    'jpg',
    'jpeg',
    'png',
    'doc',
    'xls',
    'ppt',
    'docx',
    'xlsx',
    'pptx',
    'csv'
  ]);
  /** Tamaño máximo: 10 MB — igual que UtilidadSecurityActaFile.MAX_SIZE_BYTES */
  private readonly MAX_SIZE_BYTES = 10 * 1024 * 1024;

  onAdjuntoSeleccionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (file) {
      this.procesarAdjunto(file);
    }
    // Reset input para permitir seleccionar el mismo archivo de nuevo
    input.value = '';
  }

  /** Valida y asigna el adjunto. Lo usan el clic y el arrastre. */
  procesarAdjunto(file: File): void {
    // Validar extensión
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!this.EXTENSIONES_PERMITIDAS.has(ext)) {
      this.mensajeError.set(
        `Tipo de archivo no permitido. Formatos aceptados: PDF, JPG, PNG, DOC, XLS, PPT, DOCX, XLSX, PPTX, CSV`
      );
      return;
    }
    // Validar tamaño
    if (file.size > this.MAX_SIZE_BYTES) {
      this.mensajeError.set('El archivo no puede superar 10 MB');
      return;
    }
    this.mensajeError.set(null);
    this.form.adjuntoArchivo = file;
    this.form.adjuntoNombre = file.name;
  }

  limpiarAdjunto(): void {
    this.form.adjuntoArchivo = null;
    this.form.adjuntoNombre = null;
  }

  /**
   * Legacy: lbMotivoReporte.addChangeHandler → validarSubirEvidencias(null)
   * Recalcula visibilidad del adjunto al cambiar motivo de reporte.
   */
  onMotivoReporteChange(): void {
    this.actualizarAdjuntoVisible();
  }

  /**
   * Legacy: validarSubirEvidencias(codigo)
   * Adjunto visible si código en {19,20,21}.
   * Si código==21 (NMCODIGO_CLIENTE_REPORTADO) → solo si motivo=="Posible Agrupadora".
   */
  private actualizarAdjuntoVisible(): void {
    const cod = this.form.nmconsecRespuestaLlamada;
    if (!cod || !this.NMCODIGOS_VALIDOS_ADJUNTO.includes(cod)) {
      this.adjuntoVisible = false;
      this.limpiarAdjunto();
      return;
    }
    if (cod === this.NMCODIGO_CLIENTE_REPORTADO) {
      this.adjuntoVisible = this.form.dsmotivoReporte === 'Posible Agrupadora';
    } else {
      this.adjuntoVisible = true;
    }
    if (!this.adjuntoVisible) {
      this.limpiarAdjunto();
    }
  }

  /** Establece el mensaje de error y desplaza el foco al banner. */
  private mostrarError(msg: string): void {
    this.mensajeError.set(msg);
    // Esperar un tick para que @if renderice el elemento
    setTimeout(() => this.errorBanner?.nativeElement?.focus(), 0);
  }

  // ── Validaciones ───────────────────────────────────────────────────────────

  /**
   * Legacy: saIngresar handler — validarCamposRequeridos
   * Mensaje exact: "Se deben de completar los campos obligatorios"
   */
  private validar(): string | null {
    // Legacy: tbObservacion, tbTipoCanal, tbRespuesta obligatorios siempre
    if (!this.form.dsobservaciones?.trim())
      return 'Se deben de completar los campos obligatorios';
    if (!this.form.nmconsecValoresLista)
      return 'Se deben de completar los campos obligatorios';
    if (!this.form.nmconsecRespuestaLlamada)
      return 'Se deben de completar los campos obligatorios';
    // Legacy: si isRespuestaCliente → lbMotivoReporte.selectedIndex > 0 (opción seleccionada)
    if (this.form.esRespuestaCliente && !this.form.dsmotivoReporte) {
      return 'Se deben de completar los campos obligatorios';
    }
    return null;
  }

  /**
   * Legacy: onCLickIngresarGestion — requiere cliente y oficina seleccionados antes del popup.
   * El beneficiario viaja como DNI completo (M094_CUERPOLIZA.DNI); el SP crea la persona si no existe.
   */
  private validarBeneficiario(): string | null {
    const dni = this.form.dniBeneficiario.trim();
    if (!dni || !this.form.nmconsecOficina)
      return 'Debes seleccionar el cliente y oficina para ingresar una gestion';
    if (dni.length > MAX_DNI_BENEFICIARIO)
      return `El DNI del beneficiario no puede exceder ${MAX_DNI_BENEFICIARIO} caracteres`;
    return null;
  }

  // ── Acción principal ───────────────────────────────────────────────────────

  async onIngresarGestion(): Promise<void> {
    this.mensajeError.set(null);

    const error = this.validarBeneficiario() ?? this.validar();
    if (error) {
      this.mostrarError(error);
      return;
    }

    this.loading.set(true);
    try {
      // Convertir adjunto a base64 si existe
      let dsevidencia: string | undefined;
      let dsevidenciaBase64: string | undefined;
      if (this.form.adjuntoArchivo && this.form.adjuntoNombre) {
        dsevidencia = this.form.adjuntoNombre;
        dsevidenciaBase64 = await this.fileToBase64(this.form.adjuntoArchivo);
      }

      const request: GestionLlamadaRequest = {
        nmconsecOficina: this.form.nmconsecOficina!,
        dniBeneficiario: this.form.dniBeneficiario.trim(),
        nmconsecValoresLista: this.form.nmconsecValoresLista!,
        dsobservaciones: this.form.dsobservaciones.trim(),
        dsrespondeLlamada: this.form.dsrespondeLlamada?.trim() || undefined,
        nmconsecRespuestaLlamada:
          this.form.nmconsecRespuestaLlamada ?? undefined,
        fellamada: this.combinarFechaHora(
          this.form.fechaLlamada,
          this.form.horaLlamada
        ),
        fenuevaLlamada: this.form.fechaNuevaLlamada
          ? this.combinarFechaHora(
              this.form.fechaNuevaLlamada,
              this.form.horaNuevaLlamada
            )
          : undefined,
        // Legacy: dsmotivoReporte solo si respuestaCliente=true
        dsmotivoReporte: this.form.esRespuestaCliente
          ? this.form.dsmotivoReporte?.trim() || undefined
          : undefined,
        // Legacy: Constants.SNCANCELACION_NO = 'N' — siempre en creación
        sncancelacion: 'N',
        dsevidencia,
        dsevidenciaBase64
      };

      const resultado = await this.sinOcRepository.ingresarGestion(request);

      this.gestionCreada.emit(resultado);
      this.cerrar();
    } catch (err: unknown) {
      this.mostrarError(this.extraerMensajeError(err));
    } finally {
      this.loading.set(false);
    }
  }

  // ── Cierre del diálogo ─────────────────────────────────────────────────────

  /**
   * Único punto de notificación al padre. Llamado SOLO por (onHide) de p-dialog.
   * PrimeNG dispara (onHide) tanto al hacer click en X, como cuando visible se pone en false.
   */
  onDialogHide(): void {
    // Legacy: getView().getDialog() → cierra popup; getView().reset(null) → resetea form
    this.form = this.formVacio();
    this.proxLlamadaVisible = false;
    this.adjuntoVisible = false;
    this.mensajeError.set(null);
    this.onClose.emit();
  }

  /** Cierra el diálogo programáticamente. (onHide) se encarga del reset y notificación. */
  cancelar(): void {
    this.visible.set(false);
  }

  private cerrar(): void {
    // Solo oculta — (onHide) dispara onDialogHide() que emite onClose y resetea form.
    // No emitir onClose aquí para evitar doble emisión.
    this.visible.set(false);
  }

  // ── Utilidades ─────────────────────────────────────────────────────────────

  /**
   * Convierte un File a string Base64 (sin el prefijo data:...)
   * para enviar al backend como DSEVIDENCIA_B.
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Quitar el prefijo "data:<mime>;base64,"
        resolve(result.split(',')[1] ?? result);
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Combina un Date de fecha y un Date de hora en un ISO-8601 string.
   * Replica: dfLlamada + stpAppointmentTime → FELLAMADA TIMESTAMP
   */
  private combinarFechaHora(fecha: Date | null, hora: Date | null): string {
    const d = fecha ? new Date(fecha) : new Date();
    if (hora) {
      d.setHours(hora.getHours(), hora.getMinutes(), 0, 0);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    // Serializar en hora local (sin conversión UTC) para que el backend
    // reciba el LocalDateTime exacto que el usuario ingresó
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  }

  private extraerMensajeError(err: unknown): string {
    if (err && typeof err === 'object') {
      const e = err as {
        error?: { mensaje?: string; message?: string };
        message?: string;
      };
      return (
        e.error?.mensaje ??
        e.error?.message ??
        e.message ??
        'Error al registrar la gestión. Intente de nuevo.'
      );
    }
    return 'Error al registrar la gestión. Intente de nuevo.';
  }
}
